import { useEffect, useRef, useState } from "react";
import { Button, Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { Camera24Regular, Dismiss16Regular } from "@fluentui/react-icons";

const MAX_FOTO = 5;

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(88px, 1fr))", gap: tokens.spacingHorizontalS },
  cell: { position: "relative" },
  img: {
    width: "100%",
    aspectRatio: "1",
    objectFit: "cover",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    display: "block",
  },
  remove: {
    position: "absolute",
    top: "4px",
    right: "4px",
    minWidth: "28px",
    height: "28px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  hint: { color: tokens.colorNeutralForeground3 },
});

/**
 * Selezione foto: sul telefono il sistema propone fotocamera o galleria.
 * Le anteprime sono locali; la compressione avviene al momento dell'invio.
 */
export function PhotoPicker({ files, onChange }: { files: File[]; onChange: (f: File[]) => void }) {
  const s = useStyles();
  const input = useRef<HTMLInputElement>(null);
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const u = files.map((f) => URL.createObjectURL(f));
    setUrls(u);
    return () => u.forEach((x) => URL.revokeObjectURL(x));
  }, [files]);

  const add = (list: FileList | null) => {
    if (!list) return;
    const imgs = Array.from(list).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    onChange([...files, ...imgs].slice(0, MAX_FOTO));
    if (input.current) input.current.value = "";
  };

  return (
    <div className={s.root}>
      <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => add(e.target.files)} />
      {files.length > 0 && (
        <div className={s.grid}>
          {urls.map((u, i) => (
            <div key={u} className={s.cell}>
              <img className={s.img} src={u} alt={`Foto ${i + 1}`} />
              <Button
                className={s.remove}
                size="small"
                icon={<Dismiss16Regular />}
                aria-label={`Rimuovi foto ${i + 1}`}
                onClick={() => onChange(files.filter((_, j) => j !== i))}
              />
            </div>
          ))}
        </div>
      )}
      {files.length < MAX_FOTO && (
        <Button size="large" icon={<Camera24Regular />} onClick={() => input.current?.click()}>
          {files.length ? "Aggiungi un'altra foto" : "Aggiungi foto"}
        </Button>
      )}
      <Caption1 className={s.hint}>Fotografa solo il guasto, senza persone riconoscibili. Massimo {MAX_FOTO} foto.</Caption1>
    </div>
  );
}
